import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import LoginArrow1Svg from '../../_components/atoms/Icons/LoginArrow1';
import LoginArrow2Svg from '../../_components/atoms/Icons/LoginArrow2';
import LoginOrganism from '../../_components/organisms/LoginOrganism';
import { HOME_SCREEN, LOGIN_REDIRECT } from '../../core-utils/routes';
import { loginPageMessages } from '../../core-utils/messages/loginPageMessages';
import Loader from '../../_components/molecules/Loader';
import { observer } from 'mobx-react';
import ForgotPasswordOrganism from '../../_components/organisms/ForgotPasswordOrganism';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import properties from '../../core-utils/properties';
import CryptoJS from 'crypto-js';
import LoginOTPOrganism from '../../_components/organisms/LoginOTPOrganism';

export interface LoginPageProps {}

// Moved outside component to prevent recreation on every render
const useStyles = makeStyles()({
  root: {
    display: 'flex',
    width: '1366px',
    height: '100vh',
  },
  item1: {
    background:
      'linear-gradient(134.33deg, #475CB0 -5.57%, #656BC6 47.69%, #7477C8 68.03%, #A3A2D9 102.48%, #D5D3EB 141.44%, #FFFFFF 141.46%)',
    width: '670px',
    marginRight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  item2: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  text: {
    width: '393px',
    textAlign: 'center',
  },
});

const LoginPage: React.FC<LoginPageProps> = observer(() => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const messages = loginPageMessages.LOGIN_PAGE;
  const secret = process.env.ENCRYPTION_SECRET_KEY ?? '';

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [
    invalidCredentialsErrorMessage,
    setInvalidCredentialsErrorMessage,
  ] = useState('');
  const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);

  // OTP States
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>('');
  const [isOTPError, setIsOTPError] = useState<boolean>(false);
  const [isResendOTPLoading, setIsResendOTPLoading] = useState<boolean>(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = JSON.parse(
          localStorage.getItem('token-storage') as string,
        )?.access_token;
        if (token) {
          navigate(HOME_SCREEN);
        }
      } catch {
        // ignore if not logged in
      }
    };
    checkSession();
  }, []);

  const handleLoginTextChange = (e: any, name: string) => {
    const value = e.target.value as string;
    if (name === 'email') {
      setEmail(value);
    }
    if (name === 'password') {
      setPassword(value);
    }
  };

  const validate = (value: string, name: string) => {
    if (name === 'email') {
      return value?.match(
        /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,7}$/,
      );
    } else if (name === 'password') {
      return value?.length >= 8;
    }
    return true;
  };

  const handleLoginOnclick = async () => {
    const emailErrorCheck = !validate(email, 'email');
    const passwordErrorCheck = !validate(password, 'password');

    if (emailErrorCheck) {
      setEmailErrorMessage(
        email
          ? messages.ERROR_MESSAGES.INVALID_EMAIL
          : messages.ERROR_MESSAGES.EMAIL_EMPTY,
      );
    } else {
      setEmailErrorMessage('');
    }

    if (passwordErrorCheck) {
      setPasswordErrorMessage(
        password
          ? messages.ERROR_MESSAGES.INVALID_PASSWORD
          : messages.ERROR_MESSAGES.PASSWORD_EMPTY,
      );
    } else {
      setPasswordErrorMessage('');
    }

    setEmailError(emailErrorCheck);
    setPasswordError(passwordErrorCheck);

    if (emailErrorCheck || passwordErrorCheck) {
      return;
    }

    try {
      setLoading(true);

      const payload = { username: email, password };
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(payload),
        secret,
      ).toString();

      const response = await axios.post(
        `${properties.userManagementBaseUrl}${properties.users}${properties.login}`,
        { encryptedData },
      );

      if (response.status === 200) {
        setIsOTPSent(true);
      } else {
        setInvalidCredentialsErrorMessage(
          messages.ERROR_MESSAGES.INVALID_CREDENTIALS,
        );
      }
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.message ||
        messages.ERROR_MESSAGES.INVALID_CREDENTIALS;
      setInvalidCredentialsErrorMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    try {
      const payload = { email, otp };
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(payload),
        secret,
      ).toString();

      const response = await axios.post(
        properties.userManagementBaseUrl +
          properties.otpBaseURL +
          properties.verifyOTP,
        { encryptedData },
      );

      if (response.status === 200) {
        const tokenData = response.data;
        localStorage.setItem('token-storage', JSON.stringify(tokenData));
        axios.defaults.headers.common['Authorization'] = tokenData.access_token;
        navigate(LOGIN_REDIRECT);
      }
    } catch (error: any) {
      setIsOTPError(true);
      setOtpErrorMessage(
        error?.response?.data?.message || 'Invalid OTP. Please try again.',
      );
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResendOTPLoading(true);

      const response = await axios.post(
        properties.userManagementBaseUrl +
          properties.otpBaseURL +
          properties.resendOTP,
        { email },
      );

      if (response.status === 200) {
        setIsOTPError(false);
        setOtpErrorMessage('');
      }
    } catch (error: any) {
      setIsOTPError(true);
      setOtpErrorMessage(
        error?.response?.data?.message || 'Unable to resend OTP',
      );
    } finally {
      setIsResendOTPLoading(false);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      handleLoginOnclick();
    }
  };

  const renderContent = () => {
    if (isForgotPassword) {
      return (
        <ForgotPasswordOrganism setIsForgotPassword={setIsForgotPassword} />
      );
    } else if (isOTPSent) {
      return (
        <LoginOTPOrganism
          setIsOTP={setIsOTPSent}
          email={email}
          handleResendOtp={handleResendOTP}
          handleVerifyOtp={handleVerifyOTP}
          isOTPError={isOTPError}
          errorMessage={isOTPError ? otpErrorMessage : ''}
          loading={isResendOTPLoading}
        />
      );
    } else {
      return (
        <LoginOrganism
          email={email}
          password={password}
          handleLoginTextChange={handleLoginTextChange}
          emailError={emailError}
          emailErrorMessage={emailErrorMessage}
          passwordError={passwordError}
          passwordErrorMessage={passwordErrorMessage}
          handleLoginOnclick={handleLoginOnclick}
          invalidCredentialsMessage={invalidCredentialsErrorMessage}
          setIsForgotPassword={setIsForgotPassword}
        />
      );
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      {loading ? (
        <Loader />
      ) : (
        <Grid container className={classes.root}>
          <Grid className={classes.item1}>
            <div style={{ display: 'flex' }}>
              <LoginArrow1Svg />
              <LoginArrow2Svg style={{ marginLeft: '-80px' }} />
            </div>
            <Typography
              textColor={patientAccessTheme.palette.status.white}
              fontSize="28px"
              lineHeight="33.89px"
              fontType="semiBold"
              margin="97px 0px 21px 0px"
            >
              {messages.LINEAR_GRADIENT.LABEL1}
            </Typography>
            <Typography
              textColor={patientAccessTheme.palette.status.white}
              fontSize="16px"
              lineHeight="24px"
              fontType="regular"
              className={classes.text}
            >
              {messages.LINEAR_GRADIENT.LABEL2}
            </Typography>
          </Grid>
          <Grid className={classes.item2}>
            <div style={{ marginTop: '28px' }}>{renderContent()}</div>
          </Grid>
        </Grid>
      )}
    </div>
  );
});

export default LoginPage;
